import { useCallback, useEffect, useRef, useState } from 'react';

import { useHttpFetchApi } from '../http/useHttpFetch';

export interface FilePresignedDto {
  file: File;
  destinationUrl: string;
  signedUrl: string;
  expiration: number;
}

// export const defaultPresignEndPoint = 'users';
// export function useSecureFileUrl(url?: string) {
//   if (url) {
//     const querySpacing = url.includes('?') ? '&' : '?';
//     return `${url}${querySpacing}token=${api.getAccessToken()}`;
//   }
// }

export interface UploadFile {
  file: File;
  progress: number;
  isUploading: boolean;
  request: XMLHttpRequest;
  isCompleted?: boolean;
  isError?: boolean;
  presignedResult?: FilePresignedDto;
}

export interface UploadOptions {
  presignBody: any;
}

export function useUploadFiles(presignEndPoint: string) {
  const { loading, error, postJson } = useHttpFetchApi();
  const [uploadingFiles, setUploadingFiles] = useState<UploadFile[]>([]);
  const uploadingFilesRef = useRef(uploadingFiles);
  uploadingFilesRef.current = uploadingFiles;

  const presignUrl = useCallback(
    async (file: File, options?: UploadOptions) => {
      const { presignBody = {} } = options || {};
      return postJson<FilePresignedDto>(presignEndPoint, {
        mimeType: file.type,
        ...presignBody
      });
    },
    [postJson, presignEndPoint]
  );
  const upload = useCallback(
    async (file: File, options?: UploadOptions) => {
      const presignedResult = await presignUrl(file, options);
      if (!presignedResult) {
        console.error('Presign failed');
        return;
      }
      const request = new XMLHttpRequest();
      const uploadingFile: UploadFile = {
        file,
        progress: 0,
        isUploading: true,
        request,
        presignedResult
      };
      return new Promise<UploadFile>((resolve, reject) => {
        request.upload.addEventListener('progress', event => {
          uploadingFile.progress = event.loaded / event.total;
          // console.log('uploadFile progress', uploadingFile.progress);
          setUploadingFiles(uploadingFiles => [...uploadingFiles]);
        });
        request.addEventListener('load', () => {
          uploadingFile.progress = 1;
          uploadingFile.isUploading = false;
          uploadingFile.isCompleted = true;
          // console.log('uploadFile load completed');
          setUploadingFiles(uploadingFiles => [...uploadingFiles]);
          resolve(uploadingFile);
        });
        request.addEventListener('error', event => {
          uploadingFile.progress = 0;
          uploadingFile.isUploading = false;
          uploadingFile.isError = true;
          // console.log('uploadFile error');
          setUploadingFiles(uploadingFiles => [...uploadingFiles]);
          reject(event);
        });

        setUploadingFiles(uploadingFiles => [...uploadingFiles, uploadingFile]);
        request.open('PUT', presignedResult.signedUrl);
        request.setRequestHeader('Content-Type', file.type);
        request.responseType = 'text';
        request.send(file);
      });
    },
    [presignUrl]
  );
  // When useUploadFiles hook gets unmounted abort any upload requests
  useEffect(() => {
    return () => {
      const uploadingFiles = uploadingFilesRef.current;
      for (const uploadingFile of uploadingFiles) {
        if (
          uploadingFile.request.readyState !== XMLHttpRequest.DONE ||
          uploadingFile.request.readyState !== XMLHttpRequest.UNSENT
        ) {
          uploadingFile.request.abort();
        }
      }
    };
  }, []);

  // remove an upload file and abort if uploading
  const remove = useCallback((file: File) => {
    setUploadingFiles(uploadingFiles => {
      const index = uploadingFiles.findIndex(f => f.file === file);
      if (index === -1) return uploadingFiles;
      const uploadingFile = uploadingFiles[index];
      uploadingFile.request.abort();
      uploadingFiles.splice(index, 1);
      return [...uploadingFiles];
    });
  }, []);

  const uploading = loading || uploadingFiles.some(f => f.isUploading);

  return {
    error,
    uploading,
    uploadingFiles,
    upload,
    remove
  };
}
